import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AdminEmailDialog from "@/components/AdminEmailDialog";
import { Trash2, RefreshCw, Search, Phone, Mail, MessageCircle, X, Save, AlertTriangle, ExternalLink, Download, Sparkles, Send, Wand2 } from "lucide-react";

// Domaines e-mail souvent mal saisis → domaine probablement voulu
const EMAIL_TYPO_DOMAINS = {
  "gail.com": "gmail.com", "gmai.com": "gmail.com", "gmial.com": "gmail.com",
  "gmal.com": "gmail.com", "gmil.com": "gmail.com", "gmaill.com": "gmail.com",
  "gnail.com": "gmail.com", "gmail.co": "gmail.com", "gmail.fr": "gmail.com",
  "gmaio.com": "gmail.com", "gmali.com": "gmail.com",
  "hotmal.com": "hotmail.com", "hotmai.com": "hotmail.com", "hotmial.com": "hotmail.com",
  "hotmail.co": "hotmail.com", "hotmail.fr": "hotmail.fr", "hotmil.com": "hotmail.com",
  "otmail.com": "hotmail.com", "hotmaill.com": "hotmail.com",
  "yaho.com": "yahoo.com", "yahooo.com": "yahoo.com", "yahoo.co": "yahoo.com",
  "yhaoo.com": "yahoo.com", "yaho.fr": "yahoo.fr",
  "outlok.com": "outlook.com", "outllook.com": "outlook.com", "outlook.co": "outlook.com",
  "oulook.com": "outlook.com",
  "orange.f": "orange.fr", "oranges.fr": "orange.fr", "orage.fr": "orange.fr",
  "wanadoo.f": "wanadoo.fr", "fre.fr": "free.fr", "laposte.ne": "laposte.net",
  "sfr.f": "sfr.fr", "live.f": "live.fr",
};

// Renvoie l'adresse corrigée suggérée, ou null si le domaine semble correct.
function suggestEmailFix(email) {
  const at = (email || "").trim().toLowerCase();
  const i = at.lastIndexOf("@");
  if (i < 1) return null;
  const local = email.trim().slice(0, i);
  const domain = at.slice(i + 1);
  const fixed = EMAIL_TYPO_DOMAINS[domain];
  return fixed ? `${local}@${fixed}` : null;
}

const STATUS = [
  { value: "nouveau",     label: "Nouveau",       cls: "bg-rose-100 text-rose-800 border border-rose-200" },
  { value: "a_contacter", label: "À contacter",   cls: "bg-orange-100 text-orange-800 border border-orange-200" },
  { value: "contactee",   label: "Contactée",     cls: "bg-blue-100 text-blue-800 border border-blue-200" },
  { value: "en_cours",    label: "En cours",      cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  { value: "traitee",     label: "Traitée",       cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  { value: "sans_suite",  label: "Sans suite",    cls: "bg-slate-100 text-slate-700 border border-slate-200" },
];
const PRIORITY = [
  { value: "normale",    label: "Normale",    cls: "text-slate-500" },
  { value: "importante", label: "Importante", cls: "text-amber-700" },
  { value: "urgente",    label: "Urgente",    cls: "text-rose-700" },
];
const TYPE_META = {
  "Suggestion":                          { badge: "bg-indigo-100 text-indigo-700 border border-indigo-200",   accent: "border-l-indigo-400" },
  "Demande d'information":               { badge: "bg-sky-100 text-sky-700 border border-sky-200",            accent: "border-l-sky-400" },
  "Problème avec une offre":             { badge: "bg-amber-100 text-amber-800 border border-amber-200",      accent: "border-l-amber-400" },
  "Devenir partenaire":                  { badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",accent: "border-l-emerald-400" },
  "Adhésion entreprise / association":   { badge: "bg-violet-100 text-violet-700 border border-violet-200",   accent: "border-l-violet-400" },
  "Signaler un abus":                    { badge: "bg-rose-100 text-rose-700 border border-rose-200",         accent: "border-l-rose-400" },
};
const TYPE_DEFAULT = { badge: "bg-slate-100 text-slate-700 border border-slate-200", accent: "border-l-slate-300" };
const typeMeta = (t) => TYPE_META[t] || TYPE_DEFAULT;
const labelOf = (arr, v) => arr.find((x) => x.value === v)?.label || v;
const clsOf = (arr, v) => arr.find((x) => x.value === v)?.cls || "bg-slate-100";
const whatsappHref = (phone) => {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  const withCC = digits.startsWith("0") ? "33" + digits.slice(1) : digits;
  return `https://wa.me/${withCC}`;
};

function downloadCsv(url, filename) {
  api.get(url, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  });
}

export default function AdminRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [city, setCity] = useState("");
  const [aiReply, setAiReply] = useState("");
  const [selected, setSelected] = useState(null);
  const [ab, setAb] = useState(null);

  const load = () => api.get(`/admin/requests${status?`?status=${status}`:""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { api.get("/admin/exit-intent/ab-stats").then((r) => setAb(r.data)).catch(() => {}); }, []);
  const refreshBadge = () => window.dispatchEvent(new CustomEvent("requests:refresh"));

  const openReq = (r) => {
    setSelected(r);
    if (!r.admin_read) {
      api.post(`/admin/requests/${r.id}/mark-read`).then(() => {
        setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, admin_read: true } : x)));
        refreshBadge();
      }).catch(() => {});
    }
  };

  const patch = async (id, updates) => {
    await api.patch(`/admin/requests/${id}`, updates);
    load(); refreshBadge();
  };
  const del = async (id) => {
    if (!confirm("Supprimer définitivement cette demande ?")) return;
    await api.delete(`/admin/requests/${id}`);
    toast.success("Supprimée"); setSelected(null); load(); refreshBadge();
  };

  const types = useMemo(() => [...new Set(rows.map((r) => r.problem_type).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")), [rows]);
  const cities = useMemo(() => [...new Set(rows.map((r) => r.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")), [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (priority) list = list.filter((r) => (r.priority || "normale") === priority);
    if (type) list = list.filter((r) => (r.problem_type || "") === type);
    if (city) list = list.filter((r) => (r.city || "") === city);
    if (aiReply === "sent") list = list.filter((r) => r.ai_reply?.status === "sent");
    else if (aiReply === "manual") list = list.filter((r) => r.ai_reply?.status === "manual");
    const s = q.toLowerCase().trim();
    if (!s) return list;
    return list.filter((r) => [r.name, r.email, r.phone, r.city, r.problem_type, r.message, r.assigned_to]
      .some((v) => v && String(v).toLowerCase().includes(s)));
  }, [rows, q, priority, type, city, aiReply]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    acc[r.status || "nouveau"] = (acc[r.status || "nouveau"] || 0) + 1;
    if ((r.priority || "normale") === "urgente") acc.urgente = (acc.urgente || 0) + 1;
    return acc;
  }, {}), [rows]);
  const unread = useMemo(() => rows.filter((r) => !r.admin_read).length, [rows]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-requests-title">Demandes clients</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span className="text-rose-700 font-semibold">{counts.nouveau || 0} nouveau(x)</span>
            <span className="text-orange-700 font-semibold">{counts.a_contacter || 0} à contacter</span>
            <span className="text-amber-700 font-semibold">{counts.en_cours || 0} en cours</span>
            <span className="text-emerald-700 font-semibold">{counts.traitee || 0} traitée(s)</span>
            {counts.urgente ? <span className="text-rose-700 font-bold inline-flex items-center gap-1"><AlertTriangle size={12}/> {counts.urgente} urgente(s)</span> : null}
            {unread ? <span className="text-rose-700 font-bold inline-flex items-center gap-1" data-testid="requests-unread-count"><span className="inline-block w-2 h-2 rounded-full bg-rose-600"/> {unread} non lue(s)</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Rechercher nom / email / tel…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="requests-search"/>
          </div>
          <Select value={status || "all"} onValueChange={(v)=>setStatus(v==="all"?"":v)}>
            <SelectTrigger className="w-40" data-testid="requests-status-filter"><SelectValue placeholder="Statut"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUS.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={priority || "all"} onValueChange={(v)=>setPriority(v==="all"?"":v)}>
            <SelectTrigger className="w-36" data-testid="requests-priority-filter"><SelectValue placeholder="Priorité"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes priorités</SelectItem>
              {PRIORITY.map((p)=>(<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={type || "all"} onValueChange={(v)=>setType(v==="all"?"":v)}>
            <SelectTrigger className="w-48" data-testid="requests-type-filter"><SelectValue placeholder="Type de demande"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {types.map((t)=>(<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={city || "all"} onValueChange={(v)=>setCity(v==="all"?"":v)}>
            <SelectTrigger className="w-40" data-testid="requests-city-filter"><SelectValue placeholder="Ville"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {cities.map((c)=>(<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={aiReply || "all"} onValueChange={(v)=>setAiReply(v==="all"?"":v)}>
            <SelectTrigger className="w-48" data-testid="requests-aireply-filter"><SelectValue placeholder="Réponse IA"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Réponse IA : toutes</SelectItem>
              <SelectItem value="sent">Répondues par IA</SelectItem>
              <SelectItem value="manual">À traiter manuellement</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={()=>{ load(); refreshBadge(); }} data-testid="requests-refresh"><RefreshCw size={14}/></Button>
          <Button variant="outline" onClick={()=>downloadCsv(`/admin/requests-export${status?`?status=${status}`:""}`, "demandes.csv")} data-testid="requests-export-btn">
            <Download size={14} className="mr-1"/> Export
          </Button>
        </div>
      </div>

      {/* Statistiques A/B pop-up exit-intent */}
      {ab && (ab.A?.shown || ab.B?.shown) ? (
        <div className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4" data-testid="exit-ab-stats">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-heading font-bold text-slate-900 text-sm">🧪 Pop-up « suggestion » — test A/B des accroches</div>
            {ab.winner ? (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="exit-ab-winner">
                Variante {ab.winner} en tête
              </span>
            ) : (
              <span className="text-xs text-slate-500">Résultats fiables dès 20 affichages / variante</span>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {["A", "B"].map((k) => (
              <div key={k} className={`rounded-xl bg-white border px-3 py-2 ${ab.winner === k ? "border-emerald-300 ring-1 ring-emerald-200" : "border-indigo-100"}`} data-testid={`exit-ab-${k}`}>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Variante {k}</div>
                <div className="text-lg font-extrabold text-slate-900">{ab[k]?.rate ?? 0}%</div>
                <div className="text-[11px] text-slate-500">{ab[k]?.converted ?? 0} inscrits / {ab[k]?.shown ?? 0} vues</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Desktop table */}
      <div className="mt-6 hidden md:block bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Client</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Ville</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Priorité</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const isUrg = (r.priority || "normale") === "urgente";
              return (
                <tr key={r.id}
                    className={`border-t border-slate-100 border-l-4 ${typeMeta(r.problem_type).accent} hover:bg-slate-50 cursor-pointer ${!r.admin_read?"bg-rose-50/40":""} ${isUrg?"ring-1 ring-inset ring-rose-200":""}`}
                    onClick={()=>openReq(r)} data-testid={`request-row-${r.id}`}>
                  <td className="p-3 whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleDateString("fr-FR")}<br/><span className="text-slate-400">{new Date(r.created_at).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}</span></td>
                  <td className="p-3 font-semibold">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.name}
                      {!r.admin_read && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-600 text-white" data-testid={`request-unread-pill-${r.id}`}><span className="inline-block w-1.5 h-1.5 rounded-full bg-white"/> Non lue</span>}
                      {r.ai_reply?.status === "sent" && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`request-ai-pill-${r.id}`}><Sparkles size={10}/> Répondu par IA</span>}
                    </div>
                  </td>
                  <td className="p-3 text-xs"><div>{r.email}</div><div className="text-slate-500">{r.phone || "—"}</div></td>
                  <td className="p-3">{r.city || "—"}</td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${typeMeta(r.problem_type).badge}`}>{r.problem_type || "Autre"}</span></td>
                  <td className={`p-3 text-xs font-semibold ${clsOf(PRIORITY, r.priority || "normale")}`}>{labelOf(PRIORITY, r.priority || "normale")}</td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${clsOf(STATUS, r.status)}`}>{labelOf(STATUS, r.status)}</span></td>
                  <td className="p-3 text-right">
                    <button onClick={(e)=>{ e.stopPropagation(); del(r.id); }} className="text-rose-600 hover:text-rose-800" data-testid={`request-del-${r.id}`}><Trash2 size={16}/></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">Aucune demande.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-4 md:hidden space-y-2">
        {filtered.map((r) => {
          const isUrg = (r.priority || "normale") === "urgente";
          return (
            <button key={r.id}
                    onClick={()=>openReq(r)}
                    data-testid={`request-card-${r.id}`}
                    className={`w-full text-left border border-l-4 rounded-xl p-3 ${typeMeta(r.problem_type).accent} ${isUrg?"border-rose-300":"border-slate-200"} ${!r.admin_read?"bg-rose-50/40":"bg-white"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    {!r.admin_read && <span className="shrink-0 inline-block w-2 h-2 rounded-full bg-rose-600" data-testid={`request-card-unread-${r.id}`}/>}
                    <span className="truncate">{r.name}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{r.email} · {r.city || "—"}</div>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${clsOf(STATUS, r.status)}`}>{labelOf(STATUS, r.status)}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                <span>{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeMeta(r.problem_type).badge}`}>{r.problem_type || "Autre"}</span>
                {r.ai_reply?.status === "sent" && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" data-testid={`request-card-ai-pill-${r.id}`}><Sparkles size={10}/> Répondu par IA</span>}
                {isUrg && <span className="ml-auto text-rose-700 font-bold inline-flex items-center gap-1"><AlertTriangle size={11}/> Urgente</span>}
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="text-center text-slate-500 p-6">Aucune demande.</div>}
      </div>

      {selected && (
        <RequestDrawer req={selected} onClose={()=>setSelected(null)}
                       onPatch={async (u) => { await patch(selected.id, u); setSelected({ ...selected, ...u }); toast.success("Enregistré"); }}
                       onUpdated={(u) => { setSelected({ ...selected, ...u }); load(); refreshBadge(); }}
                       onDelete={()=>del(selected.id)}/>
      )}
    </div>
  );
}


function RequestDrawer({ req, onClose, onPatch, onUpdated, onDelete }) {
  const [form, setForm] = useState({
    status: req.status || "nouveau",
    priority: req.priority || "normale",
    assigned_to: req.assigned_to || "",
    internal_notes: req.internal_notes || "",
  });
  const [guidance, setGuidance] = useState("");
  const [aiSubject, setAiSubject] = useState(req.ai_reply?.subject || "");
  const [aiBody, setAiBody] = useState(req.ai_reply?.body || "");
  const [recipient, setRecipient] = useState(req.email || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSending, setAiSending] = useState(false);

  const generateDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/admin/requests/${req.id}/ai-reply/draft`, { guidance });
      setAiSubject(data.subject || "");
      setAiBody(data.body || "");
      toast.success("Brouillon généré — relisez puis envoyez");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible");
    } finally { setAiLoading(false); }
  };

  const sendReply = async () => {
    if (!aiBody.trim()) { toast.error("Le message est vide"); return; }
    const to = (recipient || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error("L'adresse e-mail du destinataire n'est pas valide");
      return;
    }
    setAiSending(true);
    try {
      const { data } = await api.post(`/admin/requests/${req.id}/ai-reply/send`, { subject: aiSubject, body: aiBody, email: to });
      toast.success("Réponse envoyée à " + to);
      onUpdated?.({ status: "contactee", email: to, ai_reply: { status: "sent", subject: data.subject, body: data.body, at: new Date().toISOString() } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setAiSending(false); }
  };
  const dirty = form.status !== req.status || form.priority !== req.priority
    || form.assigned_to !== (req.assigned_to || "") || form.internal_notes !== (req.internal_notes || "");
  const wa = whatsappHref(req.phone);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const save = () => onPatch({ ...form, last_contact_at: form.status !== "nouveau" ? new Date().toISOString() : req.last_contact_at });
  const contactNow = () => onPatch({ status: "contactee", last_contact_at: new Date().toISOString() });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center md:items-stretch md:justify-end p-0 md:p-4" onClick={onClose}>
      <div className="bg-white w-full max-h-[92dvh] md:max-w-lg md:rounded-2xl md:my-auto md:h-auto md:max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e)=>e.stopPropagation()} data-testid="request-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Demande client</div>
            <div className="font-heading text-lg md:text-xl font-bold truncate">{req.name}</div>
            <div className="text-xs text-slate-500">{new Date(req.created_at).toLocaleString("fr-FR")}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="drawer-close"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-4">
          {/* CTA rapides */}
          <div className="grid grid-cols-3 gap-2">
            <a href={req.phone ? `tel:${req.phone}` : undefined}
               data-testid="drawer-call-btn"
               className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold ${req.phone ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
              <Phone size={14}/> Appeler
            </a>
            <AdminEmailDialog recipients={[req.email]} defaultSubject="Votre demande PROXIreduc-28"
              buttonClassName="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
              buttonLabel={<><Mail size={14}/> Email</>} testId="drawer-mail" />
            <a href={wa || undefined} target="_blank" rel="noreferrer"
               data-testid="drawer-wa-btn"
               className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold ${wa ? "bg-[#25D366] text-white hover:opacity-90" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
              <MessageCircle size={14}/> WhatsApp
            </a>
          </div>
          {req.status === "nouveau" && (
            <Button onClick={contactNow} className="w-full btn-shine text-white h-10" data-testid="drawer-mark-contacted-btn">
              Marquer comme contactée
            </Button>
          )}

          {/* Coordonnées */}
          <div className="text-sm space-y-1">
            <div><span className="font-semibold">Email :</span> {req.email}</div>
            <div><span className="font-semibold">Téléphone :</span> {req.phone || "—"}</div>
            <div><span className="font-semibold">Ville :</span> {req.city || "—"}</div>
            <div><span className="font-semibold">Type :</span> {req.problem_type}</div>
            {req.origin_page && <div className="text-xs text-slate-500 flex items-center gap-1"><ExternalLink size={11}/> Depuis <span className="font-mono">{req.origin_page}</span></div>}
            {req.last_contact_at && <div className="text-xs text-slate-500">Dernier contact : {new Date(req.last_contact_at).toLocaleString("fr-FR")}</div>}
            {req.closed_at && <div className="text-xs text-slate-500">Clôturée : {new Date(req.closed_at).toLocaleString("fr-FR")}</div>}
          </div>

          <div className="p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-line">{req.message}</div>

          {req.ai_reply && (
            <div data-testid="request-ai-reply" className={`rounded-lg border p-3 text-sm ${
              req.ai_reply.status === "sent" ? "bg-emerald-50 border-emerald-200"
              : req.ai_reply.status === "manual" ? "bg-amber-50 border-amber-200"
              : "bg-rose-50 border-rose-200"}`}>
              {req.ai_reply.status === "sent" && (
                <>
                  <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><Mail size={13}/> Réponse IA envoyée automatiquement</div>
                  {req.ai_reply.subject && <div className="mt-1 text-xs text-slate-500">Objet : {req.ai_reply.subject}</div>}
                  <div className="mt-1.5 whitespace-pre-line text-slate-700">{req.ai_reply.body}</div>
                </>
              )}
              {req.ai_reply.status === "manual" && (
                <div className="font-semibold text-amber-800">Suggestion trop spécifique — à traiter manuellement{req.ai_reply.reason ? ` (${req.ai_reply.reason})` : ""}</div>
              )}
              {req.ai_reply.status === "error" && (
                <div className="font-semibold text-rose-700">Échec de l'envoi de la réponse IA — répondez manuellement</div>
              )}
            </div>
          )}

          {/* Assistant IA — reformuler puis valider la réponse */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="ai-assistant-block">
            <div className="flex items-center gap-1.5 font-semibold text-emerald-800 text-sm">
              <Sparkles size={15} /> Assistant IA — répondre à la suggestion
            </div>
            <div>
              <Label className="text-xs text-slate-600">Précisez à l'IA ce que veut vraiment la personne</Label>
              <Textarea rows={3} value={guidance} onChange={(e)=>setGuidance(e.target.value)}
                        placeholder="Ex : elle demande en fait un bon plan pour les coiffeurs à Dreux ; explique-lui que ça arrive bientôt et invite-la à créer un compte…"
                        className="mt-1 bg-white" data-testid="ai-guidance-input" />
            </div>
            <Button onClick={generateDraft} disabled={aiLoading} variant="outline"
                    className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="ai-generate-btn">
              <Wand2 size={14} className="mr-1.5" />
              {aiLoading ? "Génération…" : (aiBody ? "Regénérer la réponse" : "Générer la réponse")}
            </Button>

            {(aiBody || aiSubject) && (
              <div className="space-y-2 pt-1" data-testid="ai-draft-editor">
                <div>
                  <Label className="text-xs text-slate-600">Objet de l'email</Label>
                  <Input value={aiSubject} onChange={(e)=>setAiSubject(e.target.value)}
                         className="mt-1 bg-white" data-testid="ai-subject-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Message (modifiable avant envoi)</Label>
                  <Textarea rows={8} value={aiBody} onChange={(e)=>setAiBody(e.target.value)}
                            className="mt-1 bg-white" data-testid="ai-body-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Adresse e-mail du destinataire (modifiable si erreur de saisie)</Label>
                  <Input type="email" value={recipient} onChange={(e)=>setRecipient(e.target.value)}
                         placeholder="exemple@gmail.com"
                         className="mt-1 bg-white" data-testid="ai-recipient-input" />
                  {recipient && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient.trim()) && (
                    <div className="mt-1 text-xs text-rose-600" data-testid="ai-recipient-error">
                      L'adresse e-mail n'est pas valide — corrigez-la avant d'envoyer.
                    </div>
                  )}
                  {recipient && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient.trim()) && suggestEmailFix(recipient) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800" data-testid="ai-recipient-typo-warning">
                      <AlertTriangle size={13} className="shrink-0" />
                      <span>Adresse à vérifier — vouliez-vous dire <strong>{suggestEmailFix(recipient)}</strong> ?</span>
                      <button type="button" onClick={() => setRecipient(suggestEmailFix(recipient))}
                              className="ml-auto rounded-md bg-amber-600 px-2 py-1 font-semibold text-white hover:bg-amber-700"
                              data-testid="ai-recipient-typo-fix">
                        Corriger
                      </button>
                    </div>
                  )}
                </div>
                <Button onClick={sendReply} disabled={aiSending} className="w-full btn-shine text-white" data-testid="ai-send-btn">
                  <Send size={14} className="mr-1.5" />
                  {aiSending ? "Envoi…" : `Valider et envoyer à ${(recipient || "").trim() || req.email}`}
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v)=>setForm((s)=>({...s, status: v}))}>
                <SelectTrigger data-testid="drawer-status"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUS.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v)=>setForm((s)=>({...s, priority: v}))}>
                <SelectTrigger data-testid="drawer-priority"><SelectValue/></SelectTrigger>
                <SelectContent>{PRIORITY.map((p)=>(<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Responsable</Label>
            <Input value={form.assigned_to} onChange={set("assigned_to")} placeholder="Prénom / initiales" data-testid="drawer-assigned"/>
          </div>
          <div>
            <Label>Notes internes</Label>
            <Textarea rows={4} value={form.internal_notes} onChange={set("internal_notes")} placeholder="Historique, actions, rappel…" data-testid="drawer-notes"/>
          </div>

          <div className="flex gap-2">
            <Button disabled={!dirty} onClick={save} className="btn-shine text-white flex-1" data-testid="drawer-save"><Save size={14} className="mr-1"/> Enregistrer</Button>
            <Button variant="outline" onClick={onDelete} className="text-rose-600 hover:text-rose-800" data-testid="drawer-delete"><Trash2 size={14}/></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
